import { useState, useEffect, useMemo } from 'react';
import {
  X,
  Calendar as CalendarIcon,
  ArrowRight,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowLeftRight,
  AlertCircle,
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from '@/hooks/useTranslation';
import { useCategories } from '@/hooks/useCategories';
import { useCurrency } from '@/contexts';
import { CustomSelect } from '@/components/ui/custom-select';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { formatLocalDate, parseLocalDate } from '@/lib/localDate';
import { suggestDescriptions } from '@/lib/descriptionSuggestions';
import type { Database } from '@/lib/database.types';

type Transaction = Database['public']['Tables']['transactions']['Row'];
type Account = Database['public']['Tables']['accounts']['Row'];

type TransactionType = 'income' | 'expense' | 'transfer';

interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  transaction?: Transaction & {
    account?: Account | null;
    to_account?: Account | null;
  };
  defaultAccountId?: string;
  transactionHistory?: Array<Pick<Transaction, 'description' | 'date' | 'category_id' | 'type'>>;
}

export function TransactionModal({
  isOpen,
  onClose,
  onSuccess,
  transaction,
  defaultAccountId,
  transactionHistory = [],
}: TransactionModalProps) {
  const { t } = useTranslation();
  const { formatAmount, formatAmountInCurrency } = useCurrency();
  const { user } = useAuth();
  const { getVisibleCategoriesGrouped, getCategoryById, getCategoryIcon } = useCategories();
  const [loading, setLoading] = useState(false);
  const [showDescriptionSuggestions, setShowDescriptionSuggestions] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [formData, setFormData] = useState({
    amount: transaction?.amount?.toString() || '',
    description: transaction?.description || '',
    date: transaction?.date ? parseLocalDate(transaction.date) : new Date(),
    type: (transaction?.type || 'expense') as TransactionType,
    category_id: transaction?.category_id || '',
    account_id: transaction?.account_id || defaultAccountId || '',
    to_account_id: transaction?.to_account_id || '',
  });

  // Get selected accounts info for display
  const selectedAccount = accounts.find((a) => a.id === formData.account_id);
  const selectedToAccount = accounts.find((a) => a.id === formData.to_account_id);

  // Get categories grouped by their category group
  const groupedCategories = useMemo(() => {
    if (formData.type === 'income') return getVisibleCategoriesGrouped('income');
    if (formData.type === 'expense') return getVisibleCategoriesGrouped('expense');
    return [];
  }, [formData.type, getVisibleCategoriesGrouped]);

  /** The categories a suggestion may point at: the ones offered right now. */
  const usableCategoryIds = useMemo(
    () => new Set(groupedCategories.flatMap(({ categories }) => categories.map((c) => c.id))),
    [groupedCategories]
  );

  const descriptionSuggestions = useMemo(() => {
    if (transaction) return [];
    return suggestDescriptions(
      formData.description,
      formData.type,
      transactionHistory,
      (categoryId) => usableCategoryIds.has(categoryId)
    );
  }, [formData.description, formData.type, transaction, transactionHistory, usableCategoryIds]);

  // Check if user has enough balance for expense/transfer
  const insufficientBalance =
    (formData.type === 'expense' || formData.type === 'transfer') &&
    selectedAccount &&
    parseFloat(formData.amount || '0') > selectedAccount.balance;

  useEffect(() => {
    if (isOpen) {
      loadAccounts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (transaction) {
      setFormData({
        amount: transaction.amount.toString(),
        description: transaction.description || '',
        date: parseLocalDate(transaction.date),
        type: transaction.type as TransactionType,
        category_id: transaction.category_id || '',
        account_id: transaction.account_id || '',
        to_account_id: transaction.to_account_id || '',
      });
    } else {
      // Reset form but keep default account
      setFormData((prev) => ({
        amount: '',
        description: '',
        date: new Date(),
        type: 'expense',
        category_id: '',
        account_id: defaultAccountId || prev.account_id || '',
        to_account_id: '',
      }));
    }
  }, [transaction, defaultAccountId]);

  const loadAccounts = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('accounts')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_hidden', false)
      .order('name');

    if (error) {
      console.error('Error loading accounts:', error);
      return;
    }

    setAccounts(data || []);

    // Auto-select first account if none selected
    if (!formData.account_id && data && data.length > 0) {
      setFormData((prev) => ({ ...prev, account_id: data[0].id }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Validation
    if (!formData.account_id) {
      alert(t('transactionModal.selectAccount'));
      return;
    }

    if (formData.type === 'transfer' && !formData.to_account_id) {
      alert(t('transactionModal.selectDestination'));
      return;
    }

    if (formData.type === 'transfer' && formData.account_id === formData.to_account_id) {
      alert(t('transactionModal.sameAccountError'));
      return;
    }

    setLoading(true);

    try {
      const dataToSave = {
        amount: typeof formData.amount === 'string' ? parseFloat(formData.amount) : formData.amount,
        description:
          formData.description ||
          (formData.type === 'transfer'
            ? `${t('transactionModal.transferDescription')} ${selectedAccount?.name} → ${selectedToAccount?.name}`
            : t('transactionModal.noDescription')),
        date: formatLocalDate(formData.date),
        type: formData.type,
        category_id: formData.type === 'transfer' ? null : formData.category_id || null,
        account_id: formData.account_id,
        to_account_id: formData.type === 'transfer' ? formData.to_account_id : null,
        user_id: user.id,
      };

      if (transaction) {
        const { error } = await supabase
          .from('transactions')
          .update(dataToSave)
          .eq('id', transaction.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from('transactions').insert([dataToSave]);

        if (error) throw error;
      }

      await onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error saving transaction:', error);
      alert('Error saving transaction. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      description: '',
      date: new Date(),
      type: 'expense',
      category_id: '',
      account_id: defaultAccountId || '',
      to_account_id: '',
    });
  };

  const handleTypeChange = (type: TransactionType) => {
    setFormData((prev) => ({
      ...prev,
      type,
      // Clear to_account_id if not transfer
      to_account_id: type === 'transfer' ? prev.to_account_id : '',
      // Clear category when changing type (categories are type-specific)
      category_id: prev.type !== type ? '' : prev.category_id,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-2xl shadow-xl max-w-lg w-full mx-4 overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700/50 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {transaction ? t('transactionModal.edit') : t('transactionModal.create')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Transaction Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('transactionModal.type')}
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => handleTypeChange('income')}
                className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl font-medium transition-all ${
                  formData.type === 'income'
                    ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-2 border-green-500'
                    : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                <ArrowDownLeft className="w-5 h-5" />
                <span className="text-sm">{t('transactions.type.income')}</span>
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('expense')}
                className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl font-medium transition-all ${
                  formData.type === 'expense'
                    ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-2 border-red-500'
                    : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                <ArrowUpRight className="w-5 h-5" />
                <span className="text-sm">{t('transactions.type.expense')}</span>
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('transfer')}
                className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl font-medium transition-all ${
                  formData.type === 'transfer'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-2 border-blue-500'
                    : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                <ArrowLeftRight className="w-5 h-5" />
                <span className="text-sm">{t('transactions.type.transfer')}</span>
              </button>
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('transactionModal.amount')}
            </label>
            <input
              type="text"
              inputMode="decimal"
              required
              value={formData.amount}
              onChange={(e) => {
                // Allow digits, dots, and commas for decimal input
                const value = e.target.value.replace(',', '.');
                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                  setFormData({ ...formData, amount: value });
                }
              }}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] text-lg font-semibold"
              placeholder="0.00"
            />
          </div>

          {/* Account Selection */}
          <div className={`${formData.type === 'transfer' ? 'space-y-4' : ''}`}>
            {/* Source Account (or just Account for income/expense) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {formData.type === 'income'
                  ? t('transactionModal.toAccount')
                  : formData.type === 'transfer'
                    ? t('transactionModal.fromAccount')
                    : t('transactionModal.fromAccount')}
              </label>
              <CustomSelect
                required
                value={formData.account_id}
                onChange={(value) => setFormData({ ...formData, account_id: value })}
                placeholder={t('transactionModal.selectAccountPlaceholder')}
                options={accounts.map((account) => ({
                  value: account.id,
                  label: `${account.name} (${formatAmountInCurrency(account.balance, account.currency)})`,
                }))}
              />
              {selectedAccount && (formData.type === 'expense' || formData.type === 'transfer') && (
                <p
                  className={`text-xs mt-1 ${insufficientBalance ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}
                >
                  {t('transactionModal.availableBalance')}:{' '}
                  {formatAmountInCurrency(selectedAccount.balance, selectedAccount.currency)}
                  {insufficientBalance && (
                    <span className="flex items-center gap-1 mt-1">
                      <AlertCircle className="w-3 h-3" />
                      {t('transactionModal.insufficientBalance')}
                    </span>
                  )}
                </p>
              )}
            </div>

            {/* Transfer Arrow & Destination */}
            {formData.type === 'transfer' && (
              <>
                <div className="flex justify-center">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <ArrowRight className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('transactionModal.toAccount')}
                  </label>
                  <CustomSelect
                    required
                    value={formData.to_account_id}
                    onChange={(value) => setFormData({ ...formData, to_account_id: value })}
                    placeholder={t('transactionModal.selectDestinationPlaceholder')}
                    options={accounts
                      .filter((a) => a.id !== formData.account_id)
                      .map((account) => ({
                        value: account.id,
                        label: `${account.name} (${formatAmountInCurrency(account.balance, account.currency)})`,
                      }))}
                  />
                  {selectedToAccount && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('transactionModal.newBalance')}:{' '}
                      {formatAmountInCurrency(
                        selectedToAccount.balance + parseFloat(formData.amount || '0'),
                        selectedToAccount.currency
                      )}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('transactionModal.description')}
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                setShowDescriptionSuggestions(true);
              }}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f]"
              placeholder={
                formData.type === 'transfer'
                  ? t('transactionModal.transferDescriptionPlaceholder')
                  : t('transactionModal.descriptionPlaceholder')
              }
            />
            {showDescriptionSuggestions && descriptionSuggestions.length > 0 && (
              <div className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-800">
                {descriptionSuggestions.map((suggestion) => {
                  // Shown, not just applied: the user picks a wording and sees
                  // which category comes with it before it lands in the form.
                  const category = suggestion.categoryId
                    ? getCategoryById(suggestion.categoryId)
                    : undefined;

                  return (
                    <button
                      key={suggestion.label}
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          description: suggestion.label,
                          // A wording that was always filed somewhere brings its
                          // category along; one that never was leaves the field
                          // as the user left it.
                          category_id: category ? category.id : prev.category_id,
                        }));
                        setShowDescriptionSuggestions(false);
                      }}
                      className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm text-gray-700 transition-colors hover:bg-[#16c47f]/10 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                    >
                      <span className="truncate">{suggestion.label}</span>
                      {category && (
                        <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {getCategoryIcon(category.icon)} {category.name}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('transactionModal.date')}
            </label>
            <div className="relative">
              <DatePicker
                selected={formData.date}
                onChange={(date) => setFormData({ ...formData, date: date || new Date() })}
                dateFormat="dd/MM/yyyy"
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f]"
                wrapperClassName="w-full"
              />
              <CalendarIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500 pointer-events-none" />
            </div>
          </div>

          {/* Category (only for income/expense) */}
          {formData.type !== 'transfer' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('transactionModal.category')}
              </label>
              <CustomSelect
                value={formData.category_id}
                onChange={(value) => setFormData({ ...formData, category_id: value })}
                placeholder={t('transactionModal.categoryPlaceholder')}
                options={groupedCategories.flatMap(({ group, categories }) =>
                  categories.map((category) => ({
                    value: category.id,
                    label: `${getCategoryIcon(category.icon)} ${category.name}`,
                    group: group.name,
                  }))
                )}
              />
            </div>
          )}

          {/* Transfer Summary */}
          {formData.type === 'transfer' &&
            selectedAccount &&
            selectedToAccount &&
            formData.amount && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                  {t('transactionModal.transferSummary')}
                </h4>
                <div className="flex items-center gap-3 text-sm">
                  <div className="flex-1">
                    <p className="text-gray-600 dark:text-gray-400">{selectedAccount.name}</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formatAmount(selectedAccount.balance)} →{' '}
                      {formatAmount(selectedAccount.balance - parseFloat(formData.amount))}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-blue-500" />
                  <div className="flex-1 text-right">
                    <p className="text-gray-600 dark:text-gray-400">{selectedToAccount.name}</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formatAmount(selectedToAccount.balance)} →{' '}
                      {formatAmount(selectedToAccount.balance + parseFloat(formData.amount))}
                    </p>
                  </div>
                </div>
              </div>
            )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-colors"
            >
              {t('transactionModal.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || insufficientBalance}
              className="flex-1 px-6 py-3 bg-[#16c47f] hover:bg-[#12b070] text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {formData.type === 'transfer' && <ArrowLeftRight className="w-4 h-4" />}
                  {transaction ? t('transactionModal.update') : t('transactionModal.submit')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
